import * as React from "react"
import { useState, useEffect, useCallback, useRef } from "react"
import { mdiCheck, mdiClose, mdiLoading, mdiUnfoldMoreHorizontal } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { cn } from "../lib/utils"
import { Button } from "./ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import { useDebounce } from "../hooks/useDebounce"
import { useTranslation } from "react-i18next"

export interface ApiSearchableSelectOption {
  value: string
  label: string
  data?: Record<string, unknown>
}

interface ApiSearchableSelectProps {
  value?: string
  placeholder?: string
  emptyMessage?: string
  searchPlaceholder?: string
  onValueChange?: (value: string) => void
  onOptionSelect?: (option: ApiSearchableSelectOption) => void
  onSearch: (query: string) => Promise<ApiSearchableSelectOption[]>
  className?: string
  /**
   * Classes for the dropdown panel. Defaults to the trigger's width; a caller
   * in a narrow column (the plan editor inspector) widens it so long names
   * stay readable.
   */
  contentClassName?: string
  contentAlign?: 'start' | 'center' | 'end'
  disabled?: boolean
  debounceMs?: number
  minSearchLength?: number
  initialOptions?: ApiSearchableSelectOption[]
  allOptions?: ApiSearchableSelectOption[]
  /** Open on mount (a click-to-edit row opens the picker already open). */
  defaultOpen?: boolean
  /** Called when the dropdown is closed without a pick (Escape, click away). */
  onDismiss?: () => void
  'data-testid'?: string
}

export function ApiSearchableSelect({
  value,
  placeholder = "Select option...",
  emptyMessage,
  searchPlaceholder = "Search...",
  onValueChange,
  onOptionSelect,
  onSearch,
  className,
  contentClassName,
  contentAlign,
  disabled = false,
  debounceMs = 300,
  minSearchLength = 1,
  initialOptions = [],
  allOptions = [],
  defaultOpen = false,
  onDismiss,
  'data-testid': dataTestId,
}: ApiSearchableSelectProps) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(defaultOpen)
  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) onDismiss?.()
  }
  const [searchValue, setSearchValue] = useState("")
  const [options, setOptions] = useState<ApiSearchableSelectOption[]>(initialOptions)
  const [isSearching, setIsSearching] = useState(false)
  const [lastSelectedOption, setLastSelectedOption] = useState<ApiSearchableSelectOption | null>(null)

  const searchInputRef = useRef<HTMLInputElement>(null)
  const onSearchRef = useRef(onSearch)
  const initialOptionsRef = useRef(initialOptions)
  const optionsRef = useRef(options)
  const debouncedSearchValue = useDebounce(searchValue, debounceMs)

  // Keep refs up to date
  useEffect(() => {
    onSearchRef.current = onSearch
  }, [onSearch])

  useEffect(() => {
    initialOptionsRef.current = initialOptions
  }, [initialOptions])

  useEffect(() => {
    optionsRef.current = options
  }, [options])

  // Find the selected option from various sources
  const selectedOption = options.find((option) => option.value === value) ||
    initialOptions.find((option) => option.value === value) ||
    allOptions.find((option) => option.value === value) ||
    (lastSelectedOption?.value === value ? lastSelectedOption : null)

  // Effect to handle API search
  useEffect(() => {
    const performSearch = async () => {
      if (debouncedSearchValue.length >= minSearchLength) {
        setIsSearching(true)
        try {
          const results = await onSearchRef.current(debouncedSearchValue)
          setOptions(results)
        } catch (error) {
          console.error('Search error:', error)
          setOptions([])
        } finally {
          setIsSearching(false)
        }
      } else if (debouncedSearchValue.length === 0) {
        setOptions(initialOptionsRef.current)
      } else {
        setOptions([])
      }
    }

    if (open) {
      performSearch()
    }
  }, [debouncedSearchValue, minSearchLength, open])

  // Reset search when dropdown closes and focus input when opens
  useEffect(() => {
    if (!open) {
      setSearchValue("")
      setOptions(initialOptionsRef.current)
    } else {
      // Focus the search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 0)
    }
  }, [open])

  const handleSelect = useCallback((selectedValue: string) => {
    if (selectedValue === value) {
      onValueChange?.("")
      onOptionSelect?.({ value: "", label: "" })
      setLastSelectedOption(null)
    } else {
      onValueChange?.(selectedValue)
      // Find the selected option to pass both value and label
      const selectedOption = [...optionsRef.current, ...initialOptionsRef.current].find(option => option.value === selectedValue)
      if (selectedOption) {
        setLastSelectedOption(selectedOption)
        if (onOptionSelect) {
          onOptionSelect(selectedOption)
        }
      }
    }
    setOpen(false)
  }, [value, onValueChange, onOptionSelect])

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onValueChange?.("")
    setLastSelectedOption(null)
  }, [onValueChange])

  const handleSearchChange = useCallback((newValue: string) => {
    setSearchValue(newValue)
  }, [])

  // A query shorter than the minimum is never sent, so "no results" would be
  // a lie: say what to type instead. A query still in its debounce window has
  // not been searched yet either.
  const belowMinimum = searchValue.length < minSearchLength
  const searchPending = !belowMinimum && searchValue !== debouncedSearchValue
  const showEmptyMessage = !isSearching && !searchPending && options.length === 0

  const getEmptyMessage = () => {
    if (belowMinimum) {
      return minSearchLength <= 1
        ? t('searchable_select.type_to_search')
        : t('searchable_select.min_chars', { count: minSearchLength })
    }
    return emptyMessage ?? t('searchable_select.no_results')
  }

  return (
    <div className={cn("flex w-full", className)}>
      <DropdownMenu open={open} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "flex-1 min-w-0 justify-between font-normal border-r-0",
              value && !disabled ? "rounded-r-none" : "",
              !value && "text-muted-foreground"
            )}
            disabled={disabled}
            data-testid={dataTestId}
          >
            <span className="truncate">
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <AppIcon path={mdiUnfoldMoreHorizontal} size="sm" tone="muted" className="shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={contentAlign}
          className={cn("w-[var(--radix-dropdown-menu-trigger-width)] p-0", contentClassName)}
          data-testid={dataTestId ? `${dataTestId}-content` : undefined}
        >
          {/* bg-popover: DropdownMenuContent (this wrapper) is not part of the
              spec 2026-09-22-04 bg-card unification — it still resolves
              bg-popover — so Command must match it explicitly here instead of
              falling back to its own now-bg-card default, or the two would
              show a visible seam in dark mode. */}
          <Command shouldFilter={false} className="bg-popover">
            <CommandInput
              ref={searchInputRef}
              placeholder={searchPlaceholder}
              value={searchValue}
              onValueChange={handleSearchChange}
              data-testid={dataTestId ? `${dataTestId}-search` : undefined}
            />
            <CommandList>
              {isSearching && (
                <div className="flex items-center justify-center py-6">
                  <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">{t('searchable_select.searching')}</span>
                </div>
              )}
              {showEmptyMessage && (
                <CommandEmpty data-testid={dataTestId ? `${dataTestId}-empty` : undefined}>{getEmptyMessage()}</CommandEmpty>
              )}
              {!isSearching && options.length > 0 && (
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => handleSelect(option.value)}
                      className="cursor-pointer"
                      data-testid={dataTestId ? `${dataTestId}-option-${option.value}` : undefined}
                    >
                      <AppIcon path={mdiCheck} size="sm" className={cn( "mr-2 ", value === option.value ? "opacity-100" : "opacity-0" )} />
                      <span className="truncate" title={option.label}>{option.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </DropdownMenuContent>
      </DropdownMenu>
      {value && !disabled && (
        <Button
          type="button"
          variant="outline"
          onClick={handleClear}
          aria-label="Clear"
          data-testid="api-searchable-select-clear"
          className="px-2 border-l-0 rounded-l-none h-auto min-h-0 hover:bg-muted flex items-center justify-center"
        >
          <AppIcon path={mdiClose} size="sm" className="opacity-70 hover:opacity-100" />
        </Button>
      )}
    </div>
  )
}